/**
 * Created by Petr Kubišta on 31.05.2017.
 */
(function (Lfl, $, undefined) {
    /**
     * @memberOf Lfl
     */
    Lfl.Tables = Lfl.Tables || {};

    (function (Tables, $, undefined) {
        if (Lfl.Files.isLoadedAlready(Lfl.Tables, 'DatatableHandler')) {
            return;
        }

        /*
         |==============================================================================================================
         | Class definition
         |==============================================================================================================
         */

        /*
         |-------------------------------------------------------------------------
         | Static members */

        /**
         * Note: This object may be unnecessary, see: DataTables::InstancesMayBeUnnecessary
         * @type {{}} A string indexed list of the datatables instances. Indexed by the table path.
         */
        Lfl.Tables.datatablesInstances = {};

        /**
         * @property {string} modalIdPrefix
         * @property {string} url
         * @property {function} modalEnabled
         * @property {string} dataIdKey
         */
         Lfl.Tables.DatatableModalDefinition = class {
            modalIdPrefix;
            url;
            modalEnabled;
            dataIdKey;

            constructor(modalIdPrefix, url, modalEnabled, dataIdKey = 'id') {
                this.modalIdPrefix = modalIdPrefix;
                this.url = url;
                this.modalEnabled = modalEnabled;
                this.dataIdKey = dataIdKey;
            }

            isModalEnabled(data) {
                return this.modalEnabled === undefined || this.modalEnabled(data);
            }
        }

        /**
         * Handles the creation of datatables plugin instance.
         *
         * @param {Object}  parameters
         * @param {string}  parameters.tablePath
         * @param {string}  [parameters.useAjax] If set to true, the datatable data will be loaded via AJAX.
         * @param {string}  [parameters.ajaxLoadUrl] URL for loading data from backend with AJAX.
         * @param {Object}  parameters.ajaxAdditionalData
         * @param {function} parameters.customRowAction Callback for adding custom row action (i.e. another button with AJAX loaded modal).
         * @param {Object}  [parameters.rowClickable] Parameters of clickable row.
         * @param {boolean} parameters.rowClickable.enabled Enable clickable row.
         * @param {string}  [parameters.rowClickable.dataUrlKey] Key in data object for URL for clickable row. Default: detailUrl.
         * @param {Array.<DatatableModalDefinition>} [parameters.modalButtons] Array of objects defining modal buttons to be handled.
         * @param {boolean}  [parameters.autoreload]
         * @param {Array}   [parameters.startingOrder] The array of arrays with column name and direction e.g., [[ 'name', 'asc' ], ['created_at', 'desc']].
         * @param {Object}  [parameters.datatableOptions] Options for the datatables plugin, see the
         * [documentation](https://datatables.net/reference/option/).
         *
         * @memberOf Lfl.Tables
         * @lends Lfl.Tables.DatatableHandler
         * @constructor
         */
        Lfl.Tables.DatatableHandler = function (parameters) {
            /*
             |----------------------------------------------------------------------------------------------------------
             | Construction
             |----------------------------------------------------------------------------------------------------------
             */
            let tablePath = parameters.tablePath;
            let $table = $(tablePath);
            const order = translateStartingOrderDefinition();

            // AJAX sourced Datatables
            const datatableEtagSessionNamePrefix = 'datatable_etag_';
            let lastAutoreloadSetTimeoutId = null;
            const dtEventsForAutoreload = 'length.dt order.dt page.dt search.dt';
            const url = parameters.ajaxLoadUrl ?? window.location.pathname.replace(/^(.*)\/$/, '$1') + '/load';
            let ajaxAdditionalData = parameters.ajaxAdditionalData ?? {};

            // We use the rest of the parameters directly.

            /*
             |----------------------------------------------------------------------------------------------------------
             | Public methods
             |----------------------------------------------------------------------------------------------------------
             */

            this.init = function () {
                // DataTable script initialization
                /**
                 * Default datatable options, see the [documentation](https://datatables.net/reference/option/).
                 * @type {{}}
                 */
                let defaultOptions = {
                    pageLength: 10,
                    serverSide: true,
                    stateSave: true,
                    stateDuration: -1,
                    processing: true,
                    drawCallback: function(settings) {
                        Lfl.Tooltips.Enabler.enableNow();
                    },
                    createdRow: function (row, data) {
                        if (parameters.rowClickable !== undefined && parameters.rowClickable.enabled) {
                            let dataUrlKey = parameters.rowClickable.dataUrlKey === undefined
                                ? 'detailUrl'
                                : parameters.rowClickable.dataUrlKey;

                            row.classList.add('clickable-row');
                            row.dataset.url = data[dataUrlKey];
                        }

                        if (parameters.modalButtons !== undefined) {
                            parameters.modalButtons.forEach(function(modalDefinition) {
                                if (modalDefinition.isModalEnabled(data)) {
                                    let itemId = data[modalDefinition.dataIdKey];
                                    let selector = row.querySelector('#' + modalDefinition.modalIdPrefix + itemId);

                                    if (selector !== null) {
                                        selector.addEventListener('click', function (event) {
                                            new Lfl.Modals.DialogLoader(modalDefinition.url).loadAndOpenDialog({
                                                parameters: {
                                                    'id': itemId
                                                }
                                            });
                                        });
                                    }
                                }
                            });
                        }

                        if (parameters.customRowAction !== undefined) {
                            parameters.customRowAction(row, data)
                        }
                    },
                    stateSaveCallback: function (settings, data) {
                        sessionStorage.setItem(tablePath, JSON.stringify(data));
                    },
                    stateLoadCallback: function (settings, callback) {
                        callback(JSON.parse(sessionStorage.getItem(tablePath)));
                    }
                };

                if (parameters.useAjax !== undefined && parameters.useAjax) {
                    defaultOptions['ajax'] = function (data, callback, settings) {
                        data = jQuery.extend(data, ajaxAdditionalData);

                        const sessionId = generateSessionIdForETag(url, data);

                        makeAjaxRequest(url, data, sessionId, false, (data) => {
                            callback(data);
                        });
                    }
                }

                let datatable = $table.dataTable(jQuery.extend({
                    //TODO MOBA II - Put this into a Lfl.Lang. ... object via js-object-init.blade
                    oLanguage: {
                        "sProcessing": Lfl.Lang.get('common/graphical_elements.datatables.sProcessing'),
                        "sLengthMenu": Lfl.Lang.get('common/graphical_elements.datatables.sLengthMenu'),
                        "sZeroRecords": Lfl.Lang.get('common/graphical_elements.datatables.sZeroRecords'),
                        "sInfo": Lfl.Lang.get('common/graphical_elements.datatables.sInfo'),
                        "sInfoEmpty": Lfl.Lang.get('common/graphical_elements.datatables.sInfoEmpty'),
                        "sInfoFiltered": Lfl.Lang.get('common/graphical_elements.datatables.sInfoFiltered'),
                        "sInfoPostFix": "",
                        "sSearch": "",
                        "sUrl": "",
                        "oPaginate": {
                            "sFirst": Lfl.Lang.get('common/graphical_elements.datatables.oPaginate.sFirst'),
                            "sPrevious": Lfl.Lang.get('common/graphical_elements.datatables.oPaginate.sPrevious'),
                            "sNext": Lfl.Lang.get('common/graphical_elements.datatables.oPaginate.sNext'),
                            "sLast": Lfl.Lang.get('common/graphical_elements.datatables.oPaginate.sLast')
                        },
                        "oAria": {
                            "sortAscending": Lfl.Lang.get('common/graphical_elements.datatables.sortAscending'),
                            "sortDescending": Lfl.Lang.get('common/graphical_elements.datatables.sortDescending')
                        }
                    },
                    order,
                }, defaultOptions, parameters.datatableOptions));

                initEvents();

                // Note: We are simply storing a jQuery object on which the client code then has to call the .api() -
                //       which means they could simply call $(datatable).api() and be off the same. Consider removing
                //       this instance caching or merging it with DatatablesResolver.
                //       Link: DataTables::InstancesMayBeUnnecessary
                Lfl.Tables.datatablesInstances[tablePath] = datatable;

                if (parameters.autoreload) {
                    $table.on('init.dt', (event) => {
                        autoreloadWithBackoff([1, 1, 1, 2, 3, 5, 60], datatable, 0, true);
                    });
                }
            };

            /**
             * @param {string} name
             * @param value
             */
            this.updateRequestParameter = function (name, value) {
                this.updateRequestParameters({[name]: value});
            }

            this.updateRequestParameters = function (parameters) {
                clearLastTimeout();

                for (const [name, value] of Object.entries(parameters)) {
                    if (!(name in ajaxAdditionalData)) {
                        throw new Error(
                            'Parameter ' + name + ' was not provided in ajaxAdditionalData but is updated during runtime'
                        );
                    }

                    ajaxAdditionalData[name] = value;
                }

                autoreloadWithBackoff([60], $table, 0, false, 'full-reset');
            }

            /*
             |----------------------------------------------------------------------------------------------------------
             | Private methods
             |----------------------------------------------------------------------------------------------------------
             */

            function initEvents() {
                FunctionsCaller.register(function () {
                    Lfl.Helpers.OnClickRedirector.registerAllRowsIn(tablePath + " tbody");
                });

                if (parameters.autoreload) {
                    $table.on('init.dt', (event) => {
                        window.addEventListener('focus', focusEventListener);
                        window.addEventListener('blur', blurEventListener);
                        $table.on(dtEventsForAutoreload, null, event, initDtEventHandler);
                    });

                    $table.on('destroy.dt', (event) => {
                        window.removeEventListener('focus', focusEventListener);
                        window.removeEventListener('blur', blurEventListener);
                        $table.off(dtEventsForAutoreload, null, initDtEventHandler);
                    });
                }
            }

            function initDtEventHandler(event) {
                clearLastTimeout();
                autoreloadWithBackoff([60], $table, 0, true);
            }

            function translateStartingOrderDefinition() {
                if (parameters.startingOrder === undefined) {
                    return [[0, 'asc']];
                }

                let order = [];
                const columns = parameters.datatableOptions.columns;

                for (const colOrder of parameters.startingOrder) {
                    const startingColName = colOrder[0];
                    const startingColOrderDirection = colOrder[1];

                    for (const colKey in columns) {
                        const colName = columns[colKey].data;

                        if (colName === startingColName) {
                            order = order.concat([[colKey, startingColOrderDirection]]);
                            break;
                        }
                    }
                }

                return order;
            }

            /**
             * @param datatable
             * @param {string} drawParam
             * @param {boolean} force
             */
            function load(datatable, drawParam, force = false) {
                let params = datatable.api().ajax.params();

                if (params === undefined) {
                    return;
                }

                const sessionId = generateSessionIdForETag(url, params);
                params = jQuery.extend(params, ajaxAdditionalData);

                if (force) {
                    removeETagFromSession(url);
                }

                makeAjaxRequest(url, params, sessionId, force === false, () => {
                    datatable.api().draw(drawParam);
                });
            }

            function makeAjaxRequest(path, params, sessionId, forETag = false, successCallback) {
                $.ajax({
                    url: path,
                    type: forETag ? 'HEAD' : 'GET',
                    data: params,
                    cache: false,
                    beforeSend: function (xhr) {
                        if (hasETagForUrl(sessionId) && forETag) {
                            xhr.setRequestHeader('If-None-Match', getETagFromSession(sessionId));
                        }
                    },
                    success: function (data, textStatus, xhr) {
                        if (xhr.status === 304) {
                            return;
                        }

                        // HTTP HEAD request does not return 304 response for some reason
                        if (forETag && hasETagForUrl(sessionId) && xhr.getResponseHeader('ETag') === getETagFromSession(sessionId)) {
                            return;
                        }

                        saveETagToSession(sessionId, xhr.getResponseHeader('ETag'));

                        successCallback(data, textStatus, xhr);
                    },
                    error: function (xhr) {
                        Lfl.Ajax.ResponseParser.reloadIfUnauthenticated(xhr);
                    }
                });
            }

            function clearLastTimeout() {
                if (lastAutoreloadSetTimeoutId !== null) {
                    clearTimeout(lastAutoreloadSetTimeoutId);
                }
            }

            function generateSessionIdForETag(path, params) {
                let paramsForETag = Object.assign({}, params);
                delete paramsForETag['draw'];

                return path + '?' + (new URLSearchParams(JSON.stringify(paramsForETag))).toString();
            }

            function focusEventListener(event) {
                clearLastTimeout();

                autoreloadWithBackoff([60], $table, 0);
            }

            function blurEventListener(event) {
                clearLastTimeout();

                autoreloadWithBackoff([600], $table, 0, true);
            }

            function saveETagToSession(requestUrl, eTag) {
                window.sessionStorage.setItem(datatableEtagSessionNamePrefix + requestUrl, eTag);
            }

            function hasETagForUrl(requestUrl) {
                return window.sessionStorage.getItem(datatableEtagSessionNamePrefix + requestUrl) !== null;
            }

            function getETagFromSession(requestUrl) {
                return window.sessionStorage.getItem(datatableEtagSessionNamePrefix + requestUrl);
            }

            function removeETagFromSession(requestUrl) {
                window.sessionStorage.removeItem(datatableEtagSessionNamePrefix + requestUrl);
            }

            /**
             * @param {Array} backoff
             * @param datatable
             * @param {int} runId
             * @param {boolean} onlySetTimeoutForNext
             * @param {string} drawParam
             */
            function autoreloadWithBackoff(backoff, datatable, runId, onlySetTimeoutForNext = false, drawParam = 'page') {
                if (onlySetTimeoutForNext === false) {
                    load(datatable, drawParam, drawParam !== 'page');
                }

                const timeoutForNextRun = backoff.length <= runId ? backoff[backoff.length - 1] : backoff[runId];

                lastAutoreloadSetTimeoutId = setTimeout(() => {
                    autoreloadWithBackoff(backoff, datatable, runId + 1);
                }, timeoutForNextRun * 1000);
            }
        };
    }(Lfl.Tables, jQuery));
}(window.Lfl = window.Lfl || {}, jQuery));
